import { ActivatedChallengeEventType, ActivatedChallengeMessagePayload, BasePayload, CreateMessageRequest, EventTargetType, FormEventType, FormMessagePayload, GetMessageResponse, MESSAGE_TEMPLATES, MessageChannel, MessagePayload, MessageRecipientGroup, MessageRecipientGroupType, ObjectId, RunType, UpdateMessageRequest } from '../types';
import { LitElement, css, html } from 'lit';
import { SMS_CHARACTER_LIMIT, SMS_EFFECTIVE_MAX_LENGTH } from '../helpers/constants';
import { customElement, property, query, state } from 'lit/decorators';
import { defaultEvent, eventTypeFromMessageRunAt, getMessageHtml, getNestedOid, getRecipientGroupType, getTemplateFromMessage, messagePlainTextDescription, messageRunAtTime, messageRunAtValue, sendOrScheduleOrganizationMessage, sendOrganizationMessagePreview, updateOrganizationMessage } from '../helpers/api/messages-api';
import { delay, isDefined, isValidString } from '../helpers/utils';

import { DateTimeRangePickerComponent } from './common/date-time-range-picker';
import { FormCheckbox } from './theme/form-checkbox';
import { ProxiSelectfield } from './theme/proxi-selectfield';
import { ProxiTextArea } from './theme/proxi-textarea';
import { ProxiTextfield } from './theme/proxi-textfield';
import { RichTextEditor } from './common/rich-text-editor';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import challengeData from '../helpers/state/challenge-data';
import { convertUTCToLocale } from '../helpers/date';
import format from 'date-fns/format';
import { getMainPage } from '../helpers/page-name';
import { timezoneAsync } from '../helpers/api/google-api';
import topicData from '../helpers/state/topic-data';
import { unsafeHTML } from 'lit/directives/unsafe-html';
import { validateSmsLength } from '../helpers/strings';

@customElement('create-message-component')
export class CreateMessageComponent extends LitElement {

    @query('#sendTiming')
    private readonly sendTimingElement!: ProxiSelectfield;


    @query('#dateTimeRangePicker')
    private readonly dateTimeRangePicker!: DateTimeRangePickerComponent;

    @query('#messageSubject')
    private readonly messageSubjectElement?: ProxiTextfield;

    @query('#messageBody')
    private readonly messageBodyElement?: ProxiTextArea;

    @query('#messageReplyTo')
    private readonly messageReplyToElement?: ProxiTextfield;

    @query('#includeUniqueLink')
    private readonly includeUniqueLinkElement?: FormCheckbox;

    @query('#includeAdmins')
    private readonly includeAdminsElement?: FormCheckbox;

    @query('#messageRichTextBody')
    private readonly messageRichTextBodyElement?: RichTextEditor;

    @query('#challengeLocation')
    private readonly challengeLocationElement?: ProxiSelectfield;

    @query('#challengeReward')
    private readonly challengeRewardElement?: ProxiSelectfield;

    @query('#thresholdInput')
    private readonly thresholdInputElement?: ProxiTextfield;

    @query('#messageType')
    private readonly messageTypeElement?: ProxiSelectfield;

    @property({ type: Object })
    private existing_message?: GetMessageResponse;

    @property()
    private message_target_type: EventTargetType = EventTargetType.ActivatedChallenge;

    @property()
    private message_target_id?: ObjectId;

    @state()
    private timezoneId: string = '';

    @state()
    private timezoneShortName: string = '';

    @state()
    private channel: MessageChannel = MessageChannel.Email;

    @state()
    private message?: CreateMessageRequest | undefined;

    @state()
    private recipientGroup: MessageRecipientGroupType | undefined;

    @state()
    private previewMode: boolean = false;

    @state()
    private reloading: boolean = false;

    @state()
    private isSending: boolean = false;

    @state()
    private readonly featureEnabled: boolean = true;

    private readonly amplitude_callsite: string = 'Create Message Component';

    private readonly bindChallengeState = new StateController(this, challengeData);

    public async setExistingMessage(message: GetMessageResponse) {
        this.reloading = true;
        await this.ensureTimezoneId();

        this.existing_message = message;
        this.channel = message.channel ?? MessageChannel.Email;
        this.message_target_type = message.entity_type;
        this.message_target_id = message.entity_id;
        this.message = {
            _id: message._id,
            channel: message.channel,
            entity_type: message.entity_type,
            entity_id: message.entity_id,
            recipient_group: message.recipient_group,
            payload: message.payload,
            run_at: message.run_at ?? 'Immediate', // Default to immediate if not specified
        };
        this.recipientGroup = getRecipientGroupType(message.recipient_group);

        this.reloading = false;
        await delay(200); // Wait for the rich text editor to be ready

        if (this.messageRichTextBodyElement) {
            this.messageRichTextBodyElement.setInnerHtmlText(this.body(this.message));
        }

        // set type of message
        if (isDefined(this.messageTypeElement)) {
            const runAtType = messageRunAtValue(this.message);
            this.messageTypeElement.value = runAtType === 'immediate' || runAtType === 'scheduled' ? 'blast' : 'automated';
        }

        if (messageRunAtValue(this.message) === 'scheduled' && isDefined(this.dateTimeRangePicker)) {
            // Check if run_at is an object with a Scheduled property
            if (typeof this.message.run_at === 'object' && isDefined(this.message.run_at) && 'Scheduled' in this.message.run_at) {
                const scheduledUTC = convertUTCToLocale(this.message.run_at.Scheduled, this.timezoneId);;
                (this.dateTimeRangePicker.startDateElement.value as any) =
                    format(scheduledUTC, 'yyyy-MM-dd');
                (this.dateTimeRangePicker.startTimeElement.value as any) = format(scheduledUTC, 'HH:mm');
            }
        }
    }

    private subjectLine(payload: MessagePayload | undefined): string {
        if (!isDefined(payload)) {
            return 'No Subject';
        }

        // Check which type of message payload we have
        if ('Form' in payload) {
            return payload.Form.subject;
        } else if ('ActivatedChallenge' in payload) {
            return payload.ActivatedChallenge.subject;
        }

        return 'No Subject';
    }

    private body(message: CreateMessageRequest | undefined): string {
        if (!isDefined(message) || !isDefined(message.payload)) {
            return 'No Body';
        }
        const payload = message.payload;

        // Check which type of message payload we have
        if ('Form' in payload) {
            return payload.Form.body;
        } else if ('ActivatedChallenge' in payload) {
            return payload.ActivatedChallenge.body;
        }

        return 'No Body';
    }

    private replyTo(message: CreateMessageRequest | undefined): string {
        if (!isDefined(message) || !isDefined(message.payload)) {
            return '';
        }
        const payload = message.payload;
        // Check which type of message payload we have
        if ('Form' in payload) {
            return payload.Form.reply_to ?? '';
        } else if ('ActivatedChallenge' in payload) {
            return payload.ActivatedChallenge.reply_to ?? '';
        }
        return '';
    }

    private includeUniqueLink(message: CreateMessageRequest | undefined): boolean {
        if (!isDefined(message) || !isDefined(message.payload)) {
            return false;
        }
        const payload = message.payload;
        // Check which type of message payload we have
        if ('Form' in payload) {
            return payload.Form.include_unique_link ?? false;
        } else if ('ActivatedChallenge' in payload) {
            return payload.ActivatedChallenge.include_unique_link ?? false;
        }
        return false;
    }

    private includeAdmins(message: CreateMessageRequest | undefined): boolean {
        if (!isDefined(message) || !isDefined(message.payload)) {
            return false;
        }
        const payload = message.payload;
        // Check which type of message payload we have
        if ('Form' in payload) {
            return payload.Form.include_map_admins ?? false;
        }
        return false;
    }

    async openPreview(message: GetMessageResponse | CreateMessageRequest | undefined) {
        if (!isDefined(message)) {
            console.error('openPreview called with undefined message');
            return;
        }
        this.updateTextElements();
        this.previewMode = true;
    }

    getRecipientGroup(
        groupType: MessageRecipientGroupType | string,
    ): MessageRecipientGroup {
        switch (groupType) {
            case MessageRecipientGroupType.ActivatedChallengeParticipants:
            case 'ActivatedChallengeParticipants':
                return 'ActivatedChallengeParticipants';

            case MessageRecipientGroupType.ActivatedChallengeLocationVisitors:
            case 'ActivatedChallengeLocationVisitors':
                return {
                    ActivatedChallengeLocationVisitors: {challenge_location_id:
                        this.challengeLocationElement?.value
                            ? { $oid: this.challengeLocationElement.value }
                            : challengeData.challengeLocations[0]?.challenge_location_info?._id ?? { $oid: '' }
                    }
                };

            case MessageRecipientGroupType.ActivatedChallengeLocationSavers:
            case 'ActivatedChallengeLocationSavers': {
                return {
                    ActivatedChallengeLocationSavers: {
                        point_id: this.challengeLocationElement?.value
                            ? challengeData.challengeLocations.find(loc =>
                                loc.challenge_location_info?._id?.$oid === this.challengeLocationElement?.value
                            )?.point_info?._id ?? { $oid: '' }
                            : challengeData.challengeLocations[0]?.point_info?._id ?? { $oid: '' }
                    }
                };
            }

            case MessageRecipientGroupType.ActivatedChallengeRewardRecipients:
            case 'ActivatedChallengeRewardRecipients':
                return {
                    ActivatedChallengeRewardRecipients: {
                        reward_id: this.challengeRewardElement?.value
                            ? { $oid: this.challengeRewardElement.value }
                            : challengeData.challenge?.rewards?.[0]?._id ?? { $oid: '' }
                    }
                };

            case MessageRecipientGroupType.ActivatedChallengeActivityCompleters:
            case 'ActivatedChallengeActivityCompleters':
                // You may need to pass an activityElement or activityId
                return {
                    ActivatedChallengeActivityCompleters: { activity_id: { $oid: '' } }
                };

            case MessageRecipientGroupType.CheckinCountZero:
            case 'CheckinCountZero':
                return 'CheckinCountZero';

            case MessageRecipientGroupType.CheckinCountFewerThan:
            case 'CheckinCountFewerThan':
                return {
                    CheckinCountFewerThan: { threshold: this.parseThresholdInput() }
                };

            case MessageRecipientGroupType.CheckinCountMoreThan:
            case 'CheckinCountMoreThan':
                return {
                    CheckinCountMoreThan: { threshold: this.parseThresholdInput() }
                };

            case MessageRecipientGroupType.SaveCountZero:
            case 'SaveCountZero':
                return 'SaveCountZero';

            case MessageRecipientGroupType.SaveCountFewerThan:
            case 'SaveCountFewerThan':
                return {
                    SaveCountFewerThan: { threshold: this.parseThresholdInput() }
                };

            case MessageRecipientGroupType.SaveCountMoreThan:
            case 'SaveCountMoreThan':
                return {
                    SaveCountMoreThan: { threshold: this.parseThresholdInput() }
                };

            case MessageRecipientGroupType.FormSubmissionOwners:
            case 'FormSubmissionOwners':
                return {
                    FormSubmissionOwners: { include_admins: false }
                };
            case MessageRecipientGroupType.FormSubmissionEmailField:
            case 'FormSubmissionEmailField': {
                const emailField = topicData.topic?.add_point_customization?.custom_fields?.find(
                    field => 'Email' in field.field_type
                );
                return {
                    FormSubmissionEmailField: { custom_field_id : emailField?._id ? { $oid: emailField._id.$oid } : { $oid: '' } }
                };
            }
            default:
                throw new Error(`Unknown recipient group type: ${groupType}`);
        }
    }

    async createMesssage(entity_id: ObjectId | undefined, entity_type: EventTargetType) {
        this.reloading = true;
        await this.ensureTimezoneId();

        this.message_target_id = entity_id;
        this.message_target_type = entity_type;

        let payload: ActivatedChallengeMessagePayload | FormMessagePayload | undefined;
        let recipient_group: MessageRecipientGroup | undefined;
        const message = MESSAGE_TEMPLATES[entity_type].scheduled[0];
        const default_load = this.message?.channel === MessageChannel.SMS ? message.default_sms_payload : message.default_email_payload;
        switch (entity_type) {
            case EventTargetType.Form:
                payload = {
                    Form: {
                        ...default_load,
                        include_unique_link: true,
                        include_map_admins: false
                    }
                };
                recipient_group = this.getRecipientGroup(MessageRecipientGroupType.FormSubmissionOwners);
                break;
            case EventTargetType.ActivatedChallenge:
                payload = {
                    ActivatedChallenge: {
                        ...default_load,
                        include_unique_link: true
                    }
                };
                recipient_group = this.getRecipientGroup(MessageRecipientGroupType.ActivatedChallengeParticipants);
                break;
        }

        if (!isDefined(entity_id) || !isDefined(entity_type) || !isDefined(payload) || !isDefined(recipient_group)) {
            console.error('createMessage called with undefined parameters:', { entity_id, entity_type, payload});
            return;
        }

        this.message = {
            _id: undefined,
            channel: this.channel,
            entity_type,
            entity_id,
            recipient_group,
            payload: payload as MessagePayload,
            run_at: 'Immediate', // Default to immediate
        };

        this.reloading = false;

        // wait for the rich text editor to be defined and then set the innerHTML
        await delay(200);
        if (this.messageRichTextBodyElement?.root) this.messageRichTextBodyElement.setInnerHtmlText(this.body(this.message));
    }

    private async ensureTimezoneId() {
        if (isValidString(this.timezoneId)) {
            return;
        }

        const timezoneInfos = await timezoneAsync(String(topicData.topic?.default_map_center.latLng.lat), String(topicData.topic?.default_map_center.latLng.lng));
        this.timezoneId = timezoneInfos[0]?.identifier;
        this.timezoneShortName = timezoneInfos[0]?.shortIdentifier;
    }


     getPreviewNumber(): string | undefined {
        const input = prompt('Enter a phone number to send the preview message to (e.g., +1234567890):');
        return input ?? undefined;
    }

    async sendPreviewMessage() {
        if (!this.message_target_id || !isDefined(this.message_target_type) || !isDefined(this.message)) {
            getMainPage().errorToastElement?.showToast('No entity found');
            return;
        }

        this.updateTextElements();

        let phone_number;
        // pop up to get phone number
        if (this.channel === MessageChannel.SMS) {
            phone_number = await this.getPreviewNumber();
        }

        if (!accountInfo.isAuthenticated) {
            getMainPage().errorToastElement?.showToast('You must be logged in to send preview messages');
            return;
        }

        await sendOrganizationMessagePreview(this.amplitude_callsite, topicData.topic?.organization_id?.$oid ?? '', this.message, phone_number);

        const infomessage = `Preview message sent to ${this.message.channel === MessageChannel.SMS ? phone_number ?? 'None' : accountInfo.account?.email ?? 'your email'}`;
        getMainPage().errorToastElement?.showToast(infomessage);
    }

    async updateMessage() {
        if (!this.message_target_id || !isDefined(this.message_target_type) || !isDefined(this.message) || !isDefined(this.existing_message)) {
            getMainPage().errorToastElement?.showToast('No entity found');
            return;
        }

        this.updateTextElements();

        // make sure run at if it is scheduled is in the future
        if (typeof this.message.run_at === 'object' && isDefined(this.message.run_at) && 'Scheduled' in this.message.run_at) {
            const scheduledDate = new Date(this.message.run_at.Scheduled);
            if (scheduledDate < new Date()) {
                getMainPage().errorToastElement?.showToast('Scheduled time must be in the future');
                return;
            }
        }

        const updateMessageRequest: UpdateMessageRequest = {
            channel: this.message.channel,
            payload: this.message.payload,
            run_at: this.message.run_at,
        };

        if (this.message.run_at === 'Immediate') {
            if (!confirm(`Are you sure you want to send this message to all participants now?`)) {
                 return;
             }
             this.isSending = true;
             getMainPage().errorToastElement?.showToast('Beginning message send process...');
             this.requestUpdate();
         }

        await updateOrganizationMessage(this.amplitude_callsite, topicData.topic?.organization_id?.$oid ?? '', this.existing_message._id.$oid, updateMessageRequest);
        this.isSending = false;

        const infomessage = this.message.run_at === 'Immediate' ? `Message sent to ${challengeData.activated_challenges.length} participants` : `Message scheduled`;
        getMainPage().errorToastElement?.showToast(infomessage);
        if (this.messageSubjectElement) {
            this.messageSubjectElement.value = '';
        }
        if (this.messageBodyElement) {
            this.messageBodyElement.value = '';
        }

        // dispatch a message sent event
        this.dispatchEvent(new CustomEvent('message-sent'));

        this.message = undefined;
        this.previewMode = false;
        this.existing_message = undefined;
    }

    async onChangeMessageType(e: Event) {
        const reward_criteria = (e.target as HTMLInputElement).value;
        if (!isDefined(this.message) || !isDefined(this.message.payload)) {
            console.warn('onChangeMessageType called with undefined message or payload');
            return;
        }
        if (reward_criteria === 'blast' && isDefined(this.message)) {
            const isTypeOfScheduled = typeof this.message.run_at === 'object' && 'Scheduled' in this.message.run_at;
            this.message.run_at = isTypeOfScheduled ? this.message.run_at : 'Immediate';
        } else if (reward_criteria === 'automated' && isDefined(this.message)) {
            const isTypeOfRunAt = typeof this.message.run_at === 'object' && 'OnEvent' in this.message.run_at;
            this.message.run_at = isTypeOfRunAt ? this.message.run_at : { OnEvent : defaultEvent(this.message) };
        }
        this.requestUpdate();

        this.maybeChangeContent();
    }

    async onChangeMessageTiming(e: Event) {
        const timing = (e.target as HTMLInputElement).value;
        if (timing === 'immediate' && isDefined(this.message)) {
            this.message.run_at = 'Immediate';
        } else if (timing === 'scheduled' && isDefined(this.message)) {
            const isTypeOfScheduled = typeof this.message.run_at === 'object' && 'Scheduled' in this.message.run_at;
            this.message.run_at = isTypeOfScheduled ? this.message.run_at : {
                Scheduled: this.oneWeekFromNow().toISOString()
            };
        }
        this.requestUpdate();

        this.maybeChangeContent();
    }

    private updateTextElements() {
        // update subject, reply_to, and body on message

        const subject = this.messageSubjectElement?.value ?? this.subjectLine(this.message?.payload) ?? '';
        const message = this.channel === MessageChannel.SMS
            ? (this.messageBodyElement?.value ?? this.body(this.message) ?? '')
            : (this.messageRichTextBodyElement?.getInnerHtmlText() ?? this.body(this.message) ?? '');

        const reply_to = this.messageReplyToElement?.value;
        const include_unique_link = this.includeUniqueLinkElement?.checked ?? true;
        const include_map_admins = this.includeAdminsElement?.checked ?? false;
        switch (this.message_target_type) {
            case EventTargetType.ActivatedChallenge:
                if (isDefined(this.message) && 'ActivatedChallenge' in this.message.payload) {
                    this.message.payload.ActivatedChallenge.subject = subject;
                    this.message.payload.ActivatedChallenge.body = message;
                    this.message.payload.ActivatedChallenge.reply_to = reply_to;
                    this.message.payload.ActivatedChallenge.include_unique_link = include_unique_link;
                    this.message.payload.ActivatedChallenge.accent_color = topicData.backgroundColor;
                    this.message.payload.ActivatedChallenge.button_text_color = topicData.textColor;
                }
                break;
            case  EventTargetType.Form:
                if (isDefined(this.message) && 'Form' in this.message.payload) {
                    this.message.payload.Form.subject = subject;
                    this.message.payload.Form.body = message;
                    this.message.payload.Form.reply_to = reply_to;
                    this.message.payload.Form.include_unique_link = include_unique_link;
                    this.message.payload.Form.include_map_admins = include_map_admins;
                    this.message.payload.Form.accent_color = topicData.backgroundColor;
                    this.message.payload.Form.button_text_color = topicData.textColor;
                }
                break;
        }
    }

    async onChangeRecipientGroup(e: Event) {
        const group = (e.target as HTMLInputElement).value as MessageRecipientGroupType;
        if (!isDefined(this.message) || !isDefined(this.message.payload)) {
            console.warn('onChangeRecipientGroup called with undefined message or payload');
            return;
        }
        this.recipientGroup = group;
        this.message.recipient_group = this.getRecipientGroup(group);

        this.maybeChangeContent();
    }

    async onChangeEventType(e: Event) {
        const eventValue = (e.target as HTMLInputElement).value;
        if (!isDefined(this.message) || !isDefined(this.message.payload)) {
            console.warn('onChangeEventType called with undefined message or payload');
            return;
        }
        switch (this.message_target_type) {
            case EventTargetType.ActivatedChallenge:
                if (Object.values(ActivatedChallengeEventType).includes(eventValue as ActivatedChallengeEventType)) {
                    this.message.run_at = { OnEvent: { ActivatedChallenge: eventValue as ActivatedChallengeEventType } };
                    switch (eventValue as ActivatedChallengeEventType) {
                        case ActivatedChallengeEventType.Registration:
                            this.recipientGroup = MessageRecipientGroupType.ActivatedChallengeParticipants;
                            this.message.recipient_group = this.getRecipientGroup(MessageRecipientGroupType.ActivatedChallengeParticipants);
                            break;
                        case ActivatedChallengeEventType.LocationCheckin:
                            this.recipientGroup = MessageRecipientGroupType.ActivatedChallengeLocationVisitors;
                            this.message.recipient_group = this.getRecipientGroup(MessageRecipientGroupType.ActivatedChallengeLocationVisitors);
                            break;
                        case ActivatedChallengeEventType.LocationSaved:
                            this.recipientGroup = MessageRecipientGroupType.ActivatedChallengeLocationSavers;
                            this.message.recipient_group = this.getRecipientGroup(MessageRecipientGroupType.ActivatedChallengeLocationSavers);
                            break;
                        case ActivatedChallengeEventType.RewardEarned:
                            this.recipientGroup = MessageRecipientGroupType.ActivatedChallengeRewardRecipients;
                            this.message.recipient_group = this.getRecipientGroup(MessageRecipientGroupType.ActivatedChallengeRewardRecipients);
                            break;
                        case ActivatedChallengeEventType.ActivityApproved:
                            this.recipientGroup = MessageRecipientGroupType.ActivatedChallengeActivityCompleters;
                            this.message.recipient_group = this.getRecipientGroup(MessageRecipientGroupType.ActivatedChallengeActivityCompleters);
                            break;
                    }
                }
                break;
            case EventTargetType.Form:
                if (Object.values(FormEventType).includes(eventValue as FormEventType)) {
                    this.message.run_at = { OnEvent: { Form: eventValue as FormEventType } };
                    this.recipientGroup = MessageRecipientGroupType.FormSubmissionOwners;
                }
                break;
        }

        this.maybeChangeContent();
    }

    maybeChangeContent() {
        if (!isDefined(this.message) || !isDefined(this.message.payload)) {
            console.warn('maybeChangeContent called with undefined message or payload');
            return;
        }
        let is_a_default_body = false;
        let is_a_default_subject = false;
        let new_payload: BasePayload | undefined;
        // find message in MESSAGE_TEMPLATES
        const  template = getTemplateFromMessage(this.message, this.message_target_type);
        const body = this.channel === MessageChannel.SMS
            ? (this.messageBodyElement?.value ?? this.body(this.message))
            : (this.messageRichTextBodyElement?.getInnerHtmlText() ?? this.body(this.message));
        const subject = this.messageSubjectElement?.value ?? this.subjectLine(this.message?.payload);

        switch (messageRunAtValue(this.message)) {
            case 'immediate':
            case 'scheduled':
                is_a_default_body =
                    MESSAGE_TEMPLATES[this.message_target_type].scheduled.some(e => e.default_email_payload.body === body || e.default_sms_payload.body === body) ||
                    MESSAGE_TEMPLATES[this.message_target_type].automated.some(e => e.default_email_payload.body === body || e.default_sms_payload.body === body);

                is_a_default_subject =
                    MESSAGE_TEMPLATES[this.message_target_type].scheduled.some(e => e.default_email_payload.subject === subject || e.default_sms_payload.subject === subject) ||
                    MESSAGE_TEMPLATES[this.message_target_type].automated.some(e => e.default_email_payload.subject === subject || e.default_sms_payload.subject === subject);

                new_payload = this.message.channel === MessageChannel.Email ? template?.default_email_payload : template?.default_sms_payload;
                break;
            case 'automated':
                is_a_default_body =
                    MESSAGE_TEMPLATES[this.message_target_type].automated.some(e => e.default_email_payload.body === body || e.default_sms_payload.body === body) ||
                    MESSAGE_TEMPLATES[this.message_target_type].scheduled.some(e => e.default_email_payload.body === body || e.default_sms_payload.body === body);

                is_a_default_subject =
                    MESSAGE_TEMPLATES[this.message_target_type].automated.some(e => e.default_email_payload.subject === subject || e.default_sms_payload.subject === subject) ||
                    MESSAGE_TEMPLATES[this.message_target_type].scheduled.some(e => e.default_email_payload.subject === subject || e.default_sms_payload.subject === subject);

                new_payload = this.message.channel === MessageChannel.Email ? template?.default_email_payload : template?.default_sms_payload;
                break;
        }

        if (is_a_default_body && isDefined(new_payload)) {
            // if it hasnt been changed by the user, set it to the new payload body
            if (this.channel === MessageChannel.SMS && isDefined(this.messageBodyElement)) {
                this.messageBodyElement.value = new_payload.body;
            } else if (this.channel === MessageChannel.Email && isDefined(this.messageRichTextBodyElement)) {
                this.messageRichTextBodyElement.setInnerHtmlText(new_payload.body);
            }
        }
        if (is_a_default_subject && isDefined(this.messageSubjectElement) && isDefined(new_payload)) {
            // if it hasnt been changed by the user, set it to the new payload subject
            this.messageSubjectElement.value = new_payload.subject;
        }
    }

    async updateScheduledTime() {
         if (messageRunAtValue(this.message) === 'scheduled' && isDefined(this.message)) {
            // if the dateTimeRangePicker is not defined, return
            if (!isDefined(this.dateTimeRangePicker) || !isDefined(this.dateTimeRangePicker.startDateElement) || !isDefined(this.dateTimeRangePicker.startTimeElement)) {
                // dont set if this isnt defined. we dont want to overwrite the run_at value
                return;
            }

            const runDate = this.dateTimeRangePicker?.startDateElement.value;
            if (!runDate) {
                getMainPage().errorToastElement?.showToast('Please select a date for the scheduled message');
                return;
            }

            // Use the formatted time or the picker value
            const runTime = this.dateTimeRangePicker?.startTimeElement.value;
            if (!runTime) {
                getMainPage().errorToastElement?.showToast('Please select a time for the scheduled message');
                return;
            }

            // Now combine them properly
            const runDateTime = new Date(`${runDate}T${runTime}:00.000`).toISOString();
            this.message.run_at = {
                Scheduled: runDateTime
            };

            // Make sure the date is in the future
            if (new Date(runDateTime) < new Date()) {
                getMainPage().errorToastElement?.showToast('Scheduled time must be in the future');
            }
        }
    }

    async sendMessageToParticipants() {

        if (!this.message_target_id || !isDefined(this.message_target_type) || !isDefined(this.message)) {
            getMainPage().errorToastElement?.showToast('No entity found');
            return;
        }

        this.updateTextElements();

        const run_at: RunType = this.message.run_at;

        if (this.message.run_at === 'Immediate') {
        if (!confirm(`Are you sure you want to send this message to participants now?`)) {
                return;
            }
            this.isSending = true;
            getMainPage().errorToastElement?.showToast('Beginning message send process...');
            this.requestUpdate();
        }

        await sendOrScheduleOrganizationMessage(this.amplitude_callsite, topicData.topic?.organization_id?.$oid ?? '', this.message);
        this.isSending = false;


        // await send_challenge_event_mail(this.amplitude_callsite, this.challenge._id.$oid, messageRequest, topicData.topicKey);
        const infomessage = run_at === 'Immediate' ? `Message on its way to participants` : `Message scheduled`;
        getMainPage().errorToastElement?.showToast(infomessage);

        if (this.messageSubjectElement) {
            this.messageSubjectElement.value = '';
        }
        if (this.messageBodyElement) {
            this.messageBodyElement.value = '';
        }

        // dispatch a message sent event
        this.dispatchEvent(new CustomEvent('message-sent'));


        this.message = undefined;
        this.previewMode = false;
    }

    // Hides a recipient option when the challenge has none of the thing it targets
    // (e.g. "when a reward is earned" with zero rewards configured) — selecting it
    // would save a message that can never find a real target.
    private hasBackingDataForRecipientGroup(recipient_group: MessageRecipientGroupType): boolean {
        switch (recipient_group) {
            case MessageRecipientGroupType.ActivatedChallengeRewardRecipients:
                return (challengeData.challenge?.rewards?.length ?? 0) > 0;
            case MessageRecipientGroupType.ActivatedChallengeLocationVisitors:
            case MessageRecipientGroupType.ActivatedChallengeLocationSavers:
                return challengeData.challengeLocations.length > 0;
            case MessageRecipientGroupType.ActivatedChallengeActivityCompleters:
                return (challengeData.challenge?.activities?.length ?? 0) > 0;
            default:
                return true;
        }
    }

    get availableMessageTypes() {
        const challengeType = challengeData.challenge?.challenge_type;
        return MESSAGE_TEMPLATES[this.message_target_type].scheduled.filter(template =>
            (!challengeType || !isDefined(template.exclude_list) || !template.exclude_list.includes(challengeType)) &&
            this.hasBackingDataForRecipientGroup(template.recipient_group)
        );
    }

    get availableMessageEvents() {
        const challengeType = challengeData.challenge?.challenge_type;
        return MESSAGE_TEMPLATES[this.message_target_type].automated.filter(template =>
            (!challengeType || !isDefined(template.exclude_list) || !template.exclude_list.includes(challengeType)) &&
            this.hasBackingDataForRecipientGroup(template.recipient_group)
        );
    }

    private async exitPreview() {
        this.previewMode = false;
        await delay(200); // Wait for the DOM to update
        if (this.messageRichTextBodyElement && isDefined(this.message)) {
            this.messageRichTextBodyElement.setInnerHtmlText(this.body(this.message));
        }
        if (isDefined(this.dateTimeRangePicker) && messageRunAtValue(this.message) === 'scheduled') {
            this.dateTimeRangePicker.startDateElement.value = format(messageRunAtTime(this.message), 'yyyy-MM-dd');
            this.dateTimeRangePicker.startTimeElement.value = format(messageRunAtTime(this.message), 'HH:mm');
        }
    }

    private async close() {
        this.previewMode = false;
        this.message = undefined;
        this.existing_message = undefined;
        this.dispatchEvent(new CustomEvent('close'));
    }

    private oneWeekFromNow(): Date {
        const now = new Date();
        const oneWeekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000); // Add 7 days in milliseconds
        return oneWeekFromNow;
    }

    private plainTextDescription(): string {
        return messagePlainTextDescription(this.message);
    }

    // these need to EXACTLY match the replacement logic on the backend for Message Customization
    get personalizationOptions() : Array<{ value: string, name: string }> {
        switch (this.message_target_type) {
            case EventTargetType.ActivatedChallenge:
                return [
                    { value: '{{{display_name}}}', name: 'Challenge Display Name' },
                    { value: '{{{email}}}', name: 'Registration Email' },
                    { value: '{{{secret_activated_challenge_link}}}', name: 'Secret User Participation Link' },
                ];
            case EventTargetType.Form:
                return [
                    { value: '{{{point_name}}}', name: 'Point Name' },
                    { value: '{{{point_description}}}', name: 'Point Description' },
                    { value: '{{{point_location}}}', name: 'Point Address' },
                    { value: '{{{secret_edit_point_url}}}', name: 'Secret Edit Link' },
                ];
            default:
                console.warn('No personalization options available for this message type');
        }
        return [];
    }

    renderReloading() {
        return html`
            <loading-element
            .loadingString=${'Loading Message...'}
            .horizontal=${true}></loading-element>`;
    }

    renderSending() {
        return html`
            <loading-element
            .loadingString=${'Sending Message. This may take a few minutes, depending on the number of participants.'}
            .horizontal=${true}></loading-element>`;
    }

    renderCreate() {
        return html`
            <div class="modal__header" @click="${async () => await this.createMesssage(this.message_target_id, this.message_target_type)}">
                <form-button variant="outline" size="sm" ?fullWidth=${true}>Create Message</form-button>
            </div>
            `
        ;
    }

    renderPreview() {
        const subject = this.subjectLine(this.message?.payload);
        if (this.channel === MessageChannel.SMS) {
            // For SMS, show simple text preview with challenge name prefix
            const challengeName = challengeData.challenge?.name ?? 'Challenge';
            const messageBody = this.body(this.message);
            return html`
                <div class="preview-container">
                    <div class="preview-header" style="display: flex; justify-content: space-between; align-items: center;">
                        <div class="preview-subject">
                            <h3>SMS Preview</h3>
                        </div>
                        <form-button
                            variant="outline"
                            size="sm"
                            class="close-button"
                            @click="${this.exitPreview}">
                            Close Preview
                        </form-button>
                    </div>
                    <div class="sms-preview-content" style="background: #f5f5f5; padding: 16px; border-radius: 8px; margin-top: 16px; font-family: monospace; border: 1px solid #ddd;">
                        <div style="font-weight: bold; margin-bottom: 8px;">${challengeName}:</div>
                        <div>${messageBody}</div><br />
                        <div style="margin-bottom: 12px;">Reply <strong>STOP</strong> to opt out</div>
                        ${this.includeUniqueLink(this.message) ? html`
                        <div style="margin-top: 16px; padding-top: 12px; border-top: 1px solid #ddd; font-size: 12px; color: #666;">
                            *User's participation link will be appended here*
                        </div>
                        ` : ''}
                    </div>
                    <br />
                    <strong>Legal Notice:</strong> By sending SMS messages, you confirm that recipients have explicitly opted in to receive text messages from your organization. "Reply STOP to opt out" will be automatically added to messages as required by regulations.
                </div>
            `;
        }

        // For Email, use existing preview
        return html`
            <div class="preview-container">
                <div class="preview-header" style="display: flex; justify-content: space-between; align-items: center;">
                    <div class="preview-subject">
                        <h3>Subject: ${subject}</h3>
                    </div>
                    <form-button
                        variant="outline"
                        size="sm"
                        class="close-button"
                        @click="${this.exitPreview}">
                        Close Preview
                    </form-button>
                </div>
                <div class="email-preview-content">
                    ${this.message_target_type === EventTargetType.ActivatedChallenge ?
                        unsafeHTML(getMessageHtml(this.message_target_type, this.message?.payload, challengeData.challenge?.registration_settings?.logo_url ?? '', challengeData.challenge?.registration_settings?.cover_image_url ?? ''))
                    : this.message_target_type === EventTargetType.Form
                        ? unsafeHTML(getMessageHtml(this.message_target_type, this.message?.payload, topicData.topic?.header_url ?? '', topicData.topic?.custom_social_media_image ?? ''))
                        : ''
                    }
                </div>
            </div>
        `;
    }


    render() {
        if (this.reloading) {
            return this.renderReloading();
        }
        if (this.isSending) {
            return this.renderSending();
        }
        if (this.message_target_id && isDefined(this.message_target_type) && !isDefined(this.existing_message) && !isDefined(this.message)) {
            return this.renderCreate();
        }

        if (this.previewMode && isDefined(this.message)) {
            return this.renderPreview();
        }

        return html`
                <div class="wrapper">
                    <h4 class="sub-title">Message Type</h4>
                        <proxi-selectfield ?disabled="${!this.featureEnabled}"
                            id="sendType"
                            outlined
                            @change="${(e: CustomEvent) => {
                                if (e.target instanceof ProxiSelectfield) {
                                    if (e.target.value === 'sms' && isDefined(this.message)) {
                                        this.channel = MessageChannel.SMS;
                                        this.message.channel = MessageChannel.SMS;
                                        // Trigger content change when switching to SMS to use defaults
                                        setTimeout(() => {
                                            this.maybeChangeContent();
                                            this.validateMessageContent();
                                        }, 100);
                                    } else if (e.target.value === 'email' && isDefined(this.message)) {
                                        this.channel = MessageChannel.Email;
                                        this.message.channel = MessageChannel.Email;
                                        // Trigger content change when switching to Email to use defaults
                                        setTimeout(() => this.maybeChangeContent(), 100);
                                    }
                                    this.requestUpdate();
                                }
                            }}"
                            value="${this.message?.channel ?? 'email'}"
                            >
                            <mwc-list-item value="sms" ?disabled="${this.message_target_type === EventTargetType.Form}">SMS (Text Message) ${this.message_target_type === EventTargetType.Form ? '— Coming Soon' : ''}</mwc-list-item>
                            <mwc-list-item value="email">Email</mwc-list-item>
                        </proxi-selectfield>
                        <proxi-selectfield ?disabled="${!this.featureEnabled}"
                            id="sendTimeType"
                            outlined
                            @selected="${this.onChangeMessageType}"
                            value="${messageRunAtValue(this.message) === 'scheduled' || messageRunAtValue(this.message) === 'immediate' ? 'blast' : 'automated'}"
                            >
                            <mwc-list-item value="blast">Blast (Send to all qualifying users now or at specified time)</mwc-list-item>
                            <mwc-list-item value="automated">Automated (Send to individual users when something happens)</mwc-list-item>
                        </proxi-selectfield>
                        ${isDefined(this.message) && (messageRunAtValue(this.message) === 'immediate' || messageRunAtValue(this.message) === 'scheduled') ? html`
                            <h4 class="sub-title">Send Timing</h4>
                                <proxi-selectfield ?disabled="${!this.featureEnabled}"
                                id="sendTiming"
                                outlined
                                @selected="${this.onChangeMessageTiming}"
                                value="${isDefined(this.message) ? messageRunAtValue(this.message) : 'immediate'}"
                                >
                                <mwc-list-item value="immediate">Send Immediately</mwc-list-item>
                                <mwc-list-item value="scheduled">Schedule for Later</mwc-list-item>
                            </proxi-selectfield>
                        ` : ''}
                        ${isDefined(this.message) && messageRunAtValue(this.message) === 'scheduled' ? html`
                            <div class="date-container">
                                <date-time-range-picker
                                    id="dateTimeRangePicker"
                                    class=""
                                    .timeOnly=${false}
                                    .dateOnly=${false}
                                    .timezoneShorthand=${this.timezoneShortName ?? ''}
                                    .showEndDateTime=${false}
                                    .allDay=${false}
                                    .hideAllDayToggle=${true}
                                    @change="${this.updateScheduledTime}"
                                    >
                                </date-time-range-picker>
                            </div>
                        ` : ''}
                        ${isDefined(this.message) && this.message.run_at === 'Immediate' || messageRunAtValue(this.message) === 'scheduled' ?
                            this.renderRecipientSelection() : this.renderEventSelection()}

                        ${isDefined(this.message) && this.recipientGroup === MessageRecipientGroupType.FormSubmissionOwners ? html`
                            <div class="checkbox-wrapper">
                                <form-checkbox
                                    id="includeAdmins"
                                    name="includeAdmins"
                                    ?checked="${isDefined(this.message) ? this.includeAdmins(this.message) : false}"
                                    >
                                </form-checkbox>
                                <span>Send Email To Map Admins <br>
                                    <span style="font-size: 10px; color: #888;">
                                    <i>(by checking this box, map administrators will also get the message.)</i>
                                </span>
                            </div>
                        `:''}

                        <p class="plain-text-description">
                            ${this.plainTextDescription()}
                        </p>

                        <h4 class="sub-title">Message Details</h4>
                        ${this.channel === MessageChannel.SMS ? html`
                            <div class="sms-info-banner" style="background: #e3f2fd; padding: 12px; border-radius: 4px; margin-bottom: 16px;">
                                <p style="margin: 0; color: #1976d2; font-size: 14px;">
                                    <strong>SMS Messaging:</strong> Recipients must have opted in to receive text messages.
                                    Character limit: ${SMS_CHARACTER_LIMIT} per message. Longer messages will be split into multiple texts.
                                </p>
                            </div>
                        ` : ''}
                         <proxi-textfield ?disabled="${!this.featureEnabled}"
                            outlined
                            class="input__challenge-title"
                            id="messageSubject"
                            type="text"
                            label="${this.channel === MessageChannel.SMS ? 'Message Name (Internal Reference)' : 'Subject Line'}"
                            placeholder="${this.channel === MessageChannel.SMS ? 'Enter a brief title for your message' : 'Enter a subject line'}"
                            value="${isDefined(this.message) ? this.subjectLine(this.message.payload) : ''}"
                            @input="${this.validateMessageContent}"
                        >
                        </proxi-textfield>
                        ${this.channel === MessageChannel.Email ? html`
                            <proxi-textfield ?disabled="${!this.featureEnabled}"
                                outlined
                                class="input__challenge-title"
                                id="messageReplyTo"
                                type="text"
                                label="Reply To / Support Email (Optional)"
                                placeholder="Enter an email address for replies"
                                value="${isDefined(this.message) ? this.replyTo(this.message) : ''}"
                            >
                            </proxi-textfield>
                        ` : ''}
                        <h4 class="sub-title">Message Content</h4>
                        ${this.channel === MessageChannel.SMS ? html`
                            <proxi-textarea
                                id="messageBody"
                                outlined
                                label="Message Content"
                                placeholder="Enter your SMS message here"
                                value="${isDefined(this.message) ? this.body(this.message) : ''}"
                                @input="${this.validateMessageContent}"
                                maxlength="${SMS_EFFECTIVE_MAX_LENGTH}"
                                charCounter="true"
                                style="margin-top: 0.25rem;"
                            >
                            </proxi-textarea>
                        ` : html`
                            <rich-text-editor
                                .allowEmbed=${false}
                                id="messageRichTextBody"
                                style="margin-top: 0.25rem;"
                                .allowPersonalization=${true}
                                .personalizationOptions=${this.personalizationOptions}
                                @input="${this.validateMessageContent}"
                                @ready="${() => {
                                    if (this.messageRichTextBodyElement?.root) {
                                        this.messageRichTextBodyElement.setInnerHtmlText(this.body(this.message));
                                        this.validateMessageContent();
                                    }
                                }}"
                                >
                            </rich-text-editor>
                        `}
                        <div class="checkbox-wrapper">
                            <form-checkbox
                                id="includeUniqueLink"
                                name="includeUniqueLink"
                                ?checked="${isDefined(this.message) ? this.includeUniqueLink(this.message) : true}"
                                >
                            </form-checkbox>
                            <span>Include Unique ${this.message_target_type === EventTargetType.Form ? 'Edit' : 'Participation'} Link in Message <br>
                                <span style="font-size: 10px; color: #888;">
                                <i>(the accurate link for each user will be appended when sent and will not work in previews)</i></span>
                            </span>
                        </div>

                        <div class="button__wrapper">
                            <div style="display: flex; align-items: center; gap: 10px; justify-content: flex-start;flex-direction: row;">
                                ${isDefined(this.existing_message) ? html`
                                    <form-button
                                        class="challeng-save-button"
                                        @click="${this.close}"
                                        size="sm"
                                    >
                                        Close
                                    </form-button>
                                ` : ''}
                                ${isDefined(this.message) ? html`
                                    <form-button
                                        class="challeng-save-button"
                                        @click="${async () => await this.openPreview(this.message)}"
                                        size="sm"
                                        variant="outline"
                                    >
                                        Preview Now
                                    </form-button>
                                ` : ''}
                                <form-button
                                    class="challeng-save-button"
                                    @click="${async () => await this.sendPreviewMessage()}"
                                    size="sm"
                                    variant="outline"
                                >
                                    Send Preview to Your ${this.message?.channel === MessageChannel.Email ? 'Email' : 'Phone'}
                                </form-button>

                            </div>
                            <form-button
                                style=""
                                ?disabled="${!this.featureEnabled}"
                                class="challeng-save-button"
                                @click="${isDefined(this.existing_message) ? this.updateMessage : this.sendMessageToParticipants}"
                                size="sm"
                            >
                                ${isDefined(this.existing_message ) ? 'Update Message' : isDefined(this.sendTimingElement) && messageRunAtValue(this.message) === 'scheduled' ? 'Schedule Message' : messageRunAtValue(this.message) === 'automated' ? 'Activate Automated Message' : 'Send Now'}
                            </form-button>
                        </div>
                </div>
            </div>
        `;
    }

    recipientTypeFromGroup() {
        return getRecipientGroupType(this.message?.recipient_group) ?? MessageRecipientGroupType.ActivatedChallengeParticipants;
    }

    renderRecipientSelection() {
        return html`
            <h4 class="sub-title">Recipients</h4>
            <proxi-selectfield ?disabled="${!this.featureEnabled}"
                id="messageType"
                outlined
                @selected="${this.onChangeRecipientGroup}"
                value="${this.recipientTypeFromGroup()}"
                >
                ${this.availableMessageTypes.map((type) => html`
                    <mwc-list-item value="${type.recipient_group}">${type.dropdown_text}</mwc-list-item>
                `)}
            </proxi-selectfield>
            ${this.renderAdditionalRecipientOptions()}
        `;
    }

    renderEventSelection() {
        return html`
            <h4 class="sub-title">Event</h4>
            <proxi-selectfield ?disabled="${!this.featureEnabled}"
                id="eventType"
                outlined
                @selected="${this.onChangeEventType}"
                value="${eventTypeFromMessageRunAt(this.message)}"
                >
                ${this.availableMessageEvents.map((type) => html`
                    <mwc-list-item value="${type.event}">${type.eventDescription}</mwc-list-item>
                `)}
            </proxi-selectfield>
            ${this.renderAdditionalRecipientOptions()}
        `;
    }

    // Usage examples:
    private getSelectedLocationOid(message: CreateMessageRequest | GetMessageResponse | undefined): string {
        return getNestedOid(message?.recipient_group, 'ActivatedChallengeLocationVisitors', 'challenge_location_id', '$oid');
    }

    private getSelectedLocationSaverOid(message: CreateMessageRequest | GetMessageResponse | undefined): string {
        const point_id = getNestedOid(message?.recipient_group, 'ActivatedChallengeLocationSavers', 'point_id', '$oid');
        return challengeData.challengeLocations.find(loc => loc.point_info?._id?.$oid === point_id)?.challenge_location_info?._id?.$oid ?? '';
    }

    private getSelectedRewardOid(message: CreateMessageRequest | GetMessageResponse | undefined): string {
        return getNestedOid(message?.recipient_group, 'ActivatedChallengeRewardRecipients', 'reward_id', '$oid');
    }

    private getSelectedActivityOid(message: CreateMessageRequest | GetMessageResponse | undefined): string {
        return getNestedOid(message?.recipient_group, 'ActivatedChallengeActivityCompleters', 'activity_id', '$oid');
    }

    private getSelectedCustomFieldOid(message: CreateMessageRequest | GetMessageResponse | undefined): string {
        return getNestedOid(message?.recipient_group, 'FormSubmissionEmailField', 'custom_field_id', '$oid');
    }

    private parseThresholdInput(): number {
        const parsed = parseInt(this.thresholdInputElement?.value ?? '1');
        return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
    }

    private getThresholdFromGroup(message: CreateMessageRequest | GetMessageResponse | undefined): string {
        const group = message?.recipient_group;
        if (!isDefined(group) || typeof group === 'string') return '1';
        for (const key of ['CheckinCountFewerThan', 'CheckinCountMoreThan', 'SaveCountFewerThan', 'SaveCountMoreThan'] as const) {
            if (key in group) {
                return String((group as any)[key]?.threshold ?? 1);
            }
        }
        return '1';
    }

    renderAdditionalRecipientOptions() {
        if (!isDefined(this.message)) {
            return '';
        }
        switch (this.recipientGroup) {
            case MessageRecipientGroupType.ActivatedChallengeParticipants:
                return '';
            case MessageRecipientGroupType.ActivatedChallengeLocationVisitors:
                return html`
                    <proxi-selectfield ?disabled="${!this.featureEnabled}"
                        id="challengeLocation"
                        outlined
                       @selected="${(e: CustomEvent) => {
                            if (!isDefined(this.message)) return;
                            this.message.recipient_group = {
                                ActivatedChallengeLocationVisitors: {
                                    challenge_location_id: {
                                        $oid: (e.target as HTMLInputElement).value
                                    }
                                }
                            };
                        }}"
                        placeholder="Select a location"
                        value="${this.getSelectedLocationOid(this.message)}"
                        >
                        ${challengeData.challengeLocations.map((location) => html`
                            <mwc-list-item value="${location.challenge_location_info?._id?.$oid ?? ''}">${location.point_info?.name ?? location.challenge_location_info?.location?.search}</mwc-list-item>
                        `)}
                    </proxi-selectfield>
                `;
            case MessageRecipientGroupType.ActivatedChallengeLocationSavers:
                return html`
                    <proxi-selectfield ?disabled="${!this.featureEnabled}"
                        id="challengeLocationSaver"
                        outlined
                        @selected="${(e: CustomEvent) => {
                            if (!isDefined(this.message)) return;
                            const challenge_location_id = (e.target as HTMLInputElement).value;
                            const point_id = challengeData.challengeLocations.find(loc => loc.challenge_location_info?._id?.$oid === challenge_location_id)?.point_info?._id;
                            if (!isDefined(point_id)) {
                                console.error('Point ID not found for selected challenge location saver');
                                return;
                            }
                            this.message.recipient_group = {
                                ActivatedChallengeLocationSavers: {
                                    point_id: {
                                        $oid: point_id.$oid
                                    }
                                }
                            };
                        }}"
                        placeholder="Select a location"
                        value="${this.getSelectedLocationSaverOid(this.message)}"
                        >
                        ${challengeData.challengeLocations.map((location) => html`
                            <mwc-list-item value="${location.challenge_location_info?._id?.$oid ?? ''}">${location.point_info?.name ?? location.challenge_location_info?.location?.search}</mwc-list-item>
                        `)}
                    </proxi-selectfield>
                `;
            case MessageRecipientGroupType.ActivatedChallengeRewardRecipients:
                return html`
                    <proxi-selectfield ?disabled="${!this.featureEnabled}"
                        id="challengeReward"
                        outlined
                        @selected="${(event: CustomEvent) => {
                            if (!isDefined(this.message)) return;
                            this.message.recipient_group = {
                                ActivatedChallengeRewardRecipients: {
                                    reward_id: {
                                        $oid: (event.target as HTMLInputElement).value
                                    }
                                }
                            };
                        }}"
                        placeholder="Select a location"
                        value="${this.getSelectedRewardOid(this.message)}"
                        >
                        ${challengeData.challenge?.rewards?.map((reward) => html`
                            <mwc-list-item value="${reward?._id?.$oid ?? ''}">${reward.title}</mwc-list-item>
                        `)}
                    </proxi-selectfield>

                `;
            case MessageRecipientGroupType.ActivatedChallengeActivityCompleters:
                return html`
                    <proxi-selectfield ?disabled="${!this.featureEnabled}"
                        id="challengeReward"
                        outlined
                        @selected="${(event: CustomEvent) => {
                            if (!isDefined(this.message)) return;
                            this.message.recipient_group = {
                                ActivatedChallengeActivityCompleters: {
                                    activity_id: {
                                        $oid: (event.target as HTMLInputElement).value
                                    }
                                }
                            };
                        }}"
                        placeholder="Select a reward"
                        value="${this.getSelectedActivityOid(this.message)}"
                        >
                        ${challengeData.challenge?.activities?.map((activity) => html`
                            <mwc-list-item value="${activity?._id?.$oid ?? ''}">${activity.name}</mwc-list-item>
                        `)}
                    </proxi-selectfield>
                `;
            case MessageRecipientGroupType.CheckinCountZero:
            case MessageRecipientGroupType.SaveCountZero:
                return '';
            case MessageRecipientGroupType.CheckinCountFewerThan:
            case MessageRecipientGroupType.CheckinCountMoreThan:
            case MessageRecipientGroupType.SaveCountFewerThan:
            case MessageRecipientGroupType.SaveCountMoreThan:
                return html`
                    <proxi-textfield
                        id="thresholdInput"
                        style="width: 99%; margin-top: 8px;"
                        outlined
                        type="number"
                        label="Threshold"
                        placeholder="Enter a number"
                        .value="${this.getThresholdFromGroup(this.message)}"
                        @input="${() => {
                            if (!isDefined(this.message) || !isDefined(this.recipientGroup)) return;
                            this.message.recipient_group = this.getRecipientGroup(this.recipientGroup);
                        }}"
                    ></proxi-textfield>
                `;
            case MessageRecipientGroupType.FormSubmissionEmailField:
                return html`
                    <proxi-selectfield ?disabled="${!this.featureEnabled}"
                        id="formSubmissionEmailField"
                        outlined
                        @selected="${(event: CustomEvent) => {
                            if (!isDefined(this.message)) return;
                            this.message.recipient_group = {
                                FormSubmissionEmailField: {
                                    custom_field_id: {
                                        $oid: (event.target as HTMLInputElement).value
                                    }
                                }
                            };
                        }}"
                        placeholder="Select a custom email field to get emails from"
                        value="${this.getSelectedCustomFieldOid(this.message)}"
                        >
                        ${topicData.topic?.add_point_customization?.custom_fields?.filter((field) => !field.archived && 'Email' in field.field_type).map((field) => html`
                            <mwc-list-item value="${field?._id?.$oid ?? ''}">${field.customizations?.header ?? field._id?.$oid}</mwc-list-item>
                        `)}
                    </proxi-selectfield>
                `;
        }
    };

    // SMS-specific methods
    private validateMessageContent() {
        if (this.channel === MessageChannel.SMS) {
            const content = this.messageBodyElement?.value ?? '';
            const validation = validateSmsLength(content);
            // Show validation error if needed
            if (!validation.isValid && validation.error) {
                getMainPage().errorToastElement?.showToast(validation.error);
            }
        }
    }


    static get styles() {
        return css`
            :host {
                --modal-width: 800px;
                font-size: 12px;
                margin-top: 10px;
            }

            .wrapper {
                display: flex;
                flex-direction: column;
                gap: 10px;
                margin-top: 10px;
            }

            .modalContent {
                position: relative;
                width: 100%;
                max-height: calc(80vh - 180px);
                height: auto;
                overflow-y: auto;
                box-sizing: border-box;
            }

            .modal__header {
                width: 100%;
                text-align: center;
                padding: 0px 20px 5px;
                color: #000000;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                box-sizing: border-box;
            }

            .modal__content {
                padding: 20px 0 5px;
                width: 100%;
                box-sizing: border-box;
            }

            .header__actions {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: flex-end;
                gap: 10px;
                box-sizing: border-box;
            }



            .header-row {
                cursor: pointer;
                width: 100%;
                align-items: center;
                font-weight: 700;
                display: flex;
                height: 40px;
                background-color: var(--theme-border-color);
                gap: 10px;
            }

            .header-row mwc-icon {
                font-size: 20px;
            }

            .header-cell,
            .data-cell {
                align-items: center;
            }

            .header-cell {
                display: flex;
                align-items: center;
                padding: 0 10px;
            }

            .header-cell.text {
                text-align: left;
                width: 35%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .header-cell.number {
                text-align: center;
                width: 15%;
                justify-content: center;
            }

            .header-cell.primary {
                flex: 1;
            }

            .data-cell {
                display: flex;
                align-items: center;
                padding: 0 10px;
            }

            .data-cell.text {
                text-align: left;
                width: 35%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .data-cell.number {
                text-align: center;
                width: 15%;
                justify-content: center;
            }

            .data-cell.primary {
                flex: 1;
            }

            .action-button {
                font-size: 16px;
                border-radius: 8px;
                margin: 10px 0;
                --mdc-typography-button-text-transform: capitalize;
            }

            .aqua-blue {
                --mdc-theme-primary: var(--main-secondary-color);
            }

            .checkbox-wrapper {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 8px;
                line-height: 120%;
                color: #102C3D;
            }


            .challenges-wrapper {
                width: 100%;
                overflow-y: auto;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;
                box-sizing: border-box;
            }

            .challenge-row {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 8px 5px;
                box-sizing: border-box;
                border-bottom: 1px solid #DADADA;
                gap: 10px;
            }

            .challenge-row:hover {
                background-color: var(--theme-border-color);
                cursor: context-menu;
            }

            .sub-title {
                margin-bottom: 5px;
                color: var(--theme-input-color);
                display: inline-flex;
                --mdc-icon-size: 16px;
            }

            .user {
                color: #102C3D;
                text-align: left;
                line-height: 125%;
            }

            .stats {
                box-sizing: border-box;
                color: #102C3D;
                text-align: center;
                line-height: 125%;
            }

            .view-more {
                cursor: pointer;
                color: #102C3D;
                text-align: right;
                font-weight: 700;
                line-height: 125%;
                text-decoration-line: underline;
            }

            .button__wrapper {
                width: 100%;
                display: flex;
                align-items: flex-start;
                justify-content: space-between;
                margin: 15px 0;
            }

            @media only screen and (max-width: 550px) {
                .modalContent {
                    width: 100%;
                    min-height: 100%;
                    max-height: 100%;
                }

                .terms__desc-wrapper {
                    max-height: 200px;
                }
            }
        `;
    }
}
